import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  ComboboxItem,
  Group,
  Loader,
  OptionsFilter,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconAdjustments,
  IconCalendarWeek,
  IconCheck,
  IconDeviceFloppy,
} from '@tabler/icons-react'

import { DEVICE_ACCESS_DENIED_MESSAGE } from '@components/general/DeviceAccessNotice/constants'
import {
  useDeviceAccessDenied,
  useDeviceQueryMutationState,
  useDeviceRefresh,
  useDeviceValvesUpdate,
  useDevicesQuery,
  useValveCommission,
  type ValveTarget,
} from '@data/device/device'
import Device, {
  DeviceIDTypes,
  Valve,
  ValveKey,
  ValveSettingsPatch,
} from '@data/device/model/device'

import ValveScheduleTab
  from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveScheduleTab'
import { OPERABLE_STATES } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/valveStateMeta'
import { ValveIdentitySettings } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveIdentitySettings'
import { useValveSettingsDrafts } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/useValveSettingsDrafts'
import { useValveRecoveryPolicy } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/useValveRecoveryPolicy'
import { ValveRecoveryPanel } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveRecoveryPanel'
import { CommissionValvesPanel } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/CommissionValvesPanel'
import { ValveStatsGrid } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveStatsGrid'
import { ValveAdvancedData } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveAdvancedData'
import { hasPermissions } from '@common/utils/validation/authValidators'
import { describeApiError } from '@common/utils/apiErrors'
import { filterDevicesWithValveErrors } from '@common/utils/helperFunctions'
import { TokenPermission } from '@data/rbac/model/rbac'
import { useValveSettings } from '@data/device/device'
import { applyValveSettings } from '@data/device/mapDevice'


// ─── Valve Operation Card ─────────────────────────────────────────────────────

interface ValveOperationCardProps {
  valve: Valve
  device: Device
  pendingState: 0 | 1 | null   // null = no pending change
  onChange: (valve: Valve) => void
  draft?: ValveSettingsPatch
  onSettingsChange: (valve: Valve, patch: ValveSettingsPatch) => void
  isSaving?: boolean
  isSettingsUnavailable?: boolean
  isUpdating?: boolean
}

const ValveOperationCard = React.memo(({
                                         valve,
                                         device,
                                         pendingState,
                                         onChange,
                                         draft,
                                         onSettingsChange,
                                         isSaving = false,
                                         isSettingsUnavailable = false,
                                         isUpdating = false,
                                       }: ValveOperationCardProps) => {
  const isOperable = OPERABLE_STATES.includes(valve.state)
  const isPending = pendingState !== null

  // When pending, show what the switch is about to become
  const switchChecked = isPending ? pendingState === 1 : valve.state === 1

  return (
    <Card withBorder radius='md' p='lg'>
      <Stack gap='md'>
        <ValveIdentitySettings
          valve={valve}
          device={device}
          draft={draft}
          onSettingsChange={onSettingsChange}
          isSaving={isSaving}
          isSettingsUnavailable={isSettingsUnavailable}
        />

        {/* Operation row */}
        <Group justify='space-between' align='center'>
          <Switch
            id={valve.key}
            label={switchChecked ? 'Open' : 'Close'}
            checked={switchChecked}
            onChange={() => onChange(valve)}
            disabled={!isOperable || isUpdating}
          />

          {isPending && (
            <Badge variant='dot' color='blue' size='sm'>
              Pending change
            </Badge>
          )}
        </Group>
      </Stack>
    </Card>
  )
})

// ─── Valve Operation Tab ──────────────────────────────────────────────────────

interface ValveOperationTabProps {
  selectedDevice: Device
  devices: Device[]
  permissions: TokenPermission[]
}

const ValveOperationTab: React.FC<ValveOperationTabProps> = ({
                                                               selectedDevice,
                                                               devices,
                                                               permissions,
                                                             }) => {
  const {
    deviceUpdating,
    deviceSendingMessage,
    error,
  } = useDeviceQueryMutationState()
  const { mutateAsync: updateValves } = useDeviceValvesUpdate()
  const { mutateAsync: loadDevice } = useDeviceRefresh()
  const { mutateAsync: commissionValves } = useValveCommission()
  const valveTarget = useMemo<ValveTarget>(
    () => ({
      kind: 'production',
      dUuid: selectedDevice.dUuid,
    }),
    [selectedDevice.dUuid],
  )

  const canReadValveSettings = hasPermissions(permissions, [
    'READ:DEVICE:PROD',
  ])
  const {
    data: valveSettings,
    error: valveSettingsError,
    isLoading: isValveSettingsLoading,
    refetch: refetchValveSettings,
  } = useValveSettings(
    selectedDevice.dUuid,
    canReadValveSettings,
  )
  const valveDevice = useMemo(
    () => ({
      ...selectedDevice,
      valves: valveSettings
        ? applyValveSettings(selectedDevice.valves, valveSettings)
        : selectedDevice.valves,
    }),
    [selectedDevice, valveSettings],
  )
  const isSettingsUnavailable =
    isValveSettingsLoading || !!valveSettingsError || !canReadValveSettings

  const {
    drafts,
    modifiedCount,
    isSavingValves,
    handleSettingsChange,
    handleDiscardValves,
    handleSaveValves,
  } = useValveSettingsDrafts(selectedDevice.dUuid)

  const {
    selectedPolicy,
    isPolicyModified,
    isSavingPolicy,
    handlePolicyChange,
    handleDiscardPolicy,
    handleSavePolicy,
    probeConfirming,
    setProbeConfirming,
    handleProbe,
    isProbing,
    isFirmwareSupported,
    minFirmwareVersion,
  } = useValveRecoveryPolicy(
    valveTarget,
    selectedDevice.valveRecovery,
    selectedDevice.firmware,
  )

  const [currentValvesState, setCurrentValvesState] = useState<{ [Key in ValveKey]: Valve }>()
  const [cooldown, setCooldown] = useState(0)
  // deviceUpdating is shared by every device action, so track which one this tab started
  const [pendingAction, setPendingAction] = useState<'update' | 'refresh' | null>(null)
  const wasSendingMessage = useRef(false)
  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const wasInCooldown = useRef(false)

  useEffect(() => {
    if (!deviceUpdating) setPendingAction(null)
  }, [deviceUpdating])

  // Re-sync local state whenever the live device data changes (post-update, device switch, etc.)
  useEffect(() => {
    if (deviceUpdating) return
    setCurrentValvesState(
      Object.values(valveDevice.valves).reduce(
        (acc, valve) => {
          acc[valve.key] = { ...valve }
          return acc
        },
        {} as { [Key in ValveKey]: Valve },
      ),
    )
  }, [valveDevice, deviceUpdating, devices, error])

  useEffect(() => {
    setCooldown(0)
    wasInCooldown.current = false
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current)
      cooldownIntervalRef.current = null
    }
  }, [selectedDevice])

  useEffect(() => {
    if (wasSendingMessage.current && !deviceSendingMessage && !error) {
      notifications.show({
        title: 'Success',
        message: 'Commission valves message sent successfully.',
        color: 'green',
        icon: <IconCheck size={16} />,
      })
      setCooldown(90)
      wasInCooldown.current = true
      cooldownIntervalRef.current = setInterval(() => {
        setCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownIntervalRef.current!)
            cooldownIntervalRef.current = null
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
    wasSendingMessage.current = deviceSendingMessage
  }, [deviceSendingMessage, error])

  // Trigger a refresh when the cooldown elapses
  useEffect(() => {
    if (cooldown === 0 && wasInCooldown.current) {
      wasInCooldown.current = false
      void loadDevice({
        deviceId: selectedDevice.dUuid,
        idType: DeviceIDTypes.dUuid,
      })
    }
  }, [cooldown])

  // Clear interval on unmount
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) clearInterval(cooldownIntervalRef.current)
    }
  }, [])

  const handleValveChange = useCallback((valve: Valve) => {
    setCurrentValvesState((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        [valve.key]: {
          ...prev[valve.key],
          state: prev[valve.key].state ? 0 : 1,
        },
      }
    })
  }, [])

  const onValvesUpdateClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!selectedDevice || !currentValvesState) return

    const newValveState = Object.values(currentValvesState).reduce((acc, valve) => {
      const liveValve = valveDevice.valves[valve.key]
      if (liveValve && valve.state !== liveValve.state) {
        acc[valve.key] = valve.state
      }
      return acc
    }, {} as any)

    if (Object.keys(newValveState).length === 0) return

    let deviceParams = {
      idType: DeviceIDTypes.deviceId,
      deviceId: selectedDevice.deviceId,
    }

    if ('dUuid' in selectedDevice) {
      deviceParams = {
        idType: DeviceIDTypes.dUuid,
        deviceId: selectedDevice.dUuid,
      }
    }

    setPendingAction('update')
    void updateValves({
      deviceId: deviceParams.deviceId,
      idType: deviceParams.idType,
      valves: newValveState,
    })
  }, [selectedDevice, currentValvesState, updateValves, valveDevice.valves])

  // Derived stats from the live device (not pending local state)
  const valves = useMemo(() => Object.values(valveDevice.valves), [valveDevice.valves])

  // currentValvesState is re-synced from the live device in an effect, so for one render after a
  // device switch — or a refresh that reports fewer valve slots — it can hold a valve key the live
  // device lacks. Here, in onValvesUpdateClick and in the valve grid, such a key is treated as
  // absent: never a pending change, never sent, never rendered.
  const didSomethingChange = useMemo(() => {
    if (!selectedDevice || !currentValvesState) return false
    return Object.values(currentValvesState).some((valve) => {
      const liveValve = valveDevice.valves[valve.key]
      return !!liveValve && valve.state !== liveValve.state
    })
  }, [currentValvesState, valveDevice.valves])

  const hasValveCommPermission = useMemo(
    () => hasPermissions(permissions, ['DEVICE:VALVE:COMM']),
    [permissions],
  )

  const canCommissionValves = useMemo(
    () => selectedDevice.deviceVendor === 'SMARTFLOW' && hasValveCommPermission,
    [selectedDevice.deviceVendor, hasValveCommPermission],
  )

  // const handleCommissionValves = useCallback(() => {
  //   onCommissionValves(selectedDevice.deviceId)
  //   setCommissionSent(true)
  // }, [onCommissionValves, selectedDevice.deviceId])

  const handleCommissionValves = async () => {
    try {
      await commissionValves(valveTarget)
    } catch (error) {
      notifications.show({
        title: 'Valve commissioning failed',
        message: describeApiError(error, 'Failed to commission the valves.'),
        color: 'red',
      })
    }
  }

  const handleRefreshData = () => {
    setPendingAction('refresh')
    void loadDevice({
      deviceId: selectedDevice.dUuid,
      idType: DeviceIDTypes.dUuid,
    })

    if (canReadValveSettings) {
      void refetchValveSettings().catch(() => undefined)
    }
  }

  if (!currentValvesState) return null

  return (
    <Stack gap='lg' mt='md'>
      {/* Summary stats */}
      <ValveStatsGrid valves={valves} />

      {/* Valves */}
      <Card withBorder p='md'>
        <Stack gap='md'>
          <Group justify='space-between'>
            <Group gap='xs'>
              <Text fw={600}>Valves</Text>

              {isValveSettingsLoading && <Loader size='xs' />}
            </Group>

            {hasValveCommPermission && (
              <ValveAdvancedData target={valveTarget} firmware={selectedDevice.firmware} />
            )}
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }}>
            {Object.values(currentValvesState)
              .filter((valve) =>
                valve.state !== 7 && valve.state !== null && !!valveDevice.valves[valve.key],
              )
              .map((valve) => {
                const liveValve = valveDevice.valves[valve.key]
                const hasPendingChange = valve.state !== liveValve.state
                const pendingState = hasPendingChange ? (valve.state as 0 | 1) : null

                return (
                  <ValveOperationCard
                    key={`${selectedDevice.deviceId}-${valve.key}`}
                    valve={liveValve}
                    device={valveDevice}
                    pendingState={pendingState}
                    onChange={handleValveChange}
                    draft={drafts[valve.key]}
                    onSettingsChange={handleSettingsChange}
                    isSaving={isSavingValves}
                    isSettingsUnavailable={isSettingsUnavailable}
                    isUpdating={pendingAction === 'update'}
                  />
                )
              })}
          </SimpleGrid>

          {valveSettingsError && (
            <Alert color='red'>
              {describeApiError(valveSettingsError, 'Unable to load valve settings.')}
            </Alert>
          )}

          <Group gap='md'>
            <Button
              variant='filled'
              disabled={!didSomethingChange}
              type='submit'
              onClick={onValvesUpdateClick}
              loading={pendingAction === 'update'}
              w='fit-content'
            >
              Update
            </Button>

            <Button
              variant='light'
              onClick={handleRefreshData}
              disabled={cooldown > 0}
              loading={pendingAction === 'refresh'}
            >
              Refresh
            </Button>
          </Group>

          <Group justify='flex-end' gap='sm'>
            <Button
              variant='default'
              onClick={handleDiscardValves}
              disabled={!modifiedCount || isSavingValves}
            >
              Discard
            </Button>

            <Button
              onClick={handleSaveValves}
              disabled={!modifiedCount || isSettingsUnavailable}
              loading={isSavingValves}
              leftSection={<IconDeviceFloppy size={16} />}
            >
              {modifiedCount
                ? `Save changes (${modifiedCount})`
                : 'Save changes'}
            </Button>
          </Group>
        </Stack>
      </Card>

      {/* Valve Recovery Mode + Valve Probe */}
      {hasValveCommPermission && (
        <ValveRecoveryPanel
          selectedPolicy={selectedPolicy}
          isPolicyModified={isPolicyModified}
          isSavingPolicy={isSavingPolicy}
          onPolicyChange={handlePolicyChange}
          onDiscardPolicy={handleDiscardPolicy}
          onSavePolicy={handleSavePolicy}
          probeConfirming={probeConfirming}
          onProbeConfirmingChange={setProbeConfirming}
          onProbe={handleProbe}
          isProbing={isProbing}
          isFirmwareSupported={isFirmwareSupported}
          minFirmwareVersion={minFirmwareVersion}
        />
      )}

      {/* Commission Valves */}
      {canCommissionValves && (
        <CommissionValvesPanel
          onCommission={handleCommissionValves}
          isSending={deviceSendingMessage}
          disabled={deviceUpdating || cooldown > 0}
          cooldown={cooldown}
        />
      )}
    </Stack>
  )
}

// ─── ValvesManagement ─────────────────────────────────────────────────────────

interface IValvesManagementProps {
  devices: Device[]
  permissions: TokenPermission[]
}

// Mantine's default filter matches option labels only, and the device options are labelled
// with the device name alone. Match the option value — the device ID — as well, so a device
// can be found by either. Options here are always flat (no groups), hence the cast; `limit`
// is honoured to keep the same contract as the default filter.
const filterByNameOrId: OptionsFilter = ({ options, search, limit }) => {
  const query = search.trim().toLowerCase()

  return (options as ComboboxItem[])
    .filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        option.value.toLowerCase().includes(query),
    )
    .slice(0, limit)
}

const ValvesManagement: React.FC<IValvesManagementProps> = ({ devices, permissions }) => {
  const { isLoading: loading } = useDevicesQuery()
  const { deviceUpdating } = useDeviceQueryMutationState()
  const accessDenied = useDeviceAccessDenied()

  const [selectedDevice, setSelectedDevice] = useState<Device>()
  const [activeTab, setActiveTab] = useState<string | null>('operation')
  const [errorsOnly, setErrorsOnly] = useState(false)

  // Keep selectedDevice in sync when the devices list refreshes
  useEffect(() => {
    if (!selectedDevice) return
    const updated = devices.find((d) => d.deviceId === selectedDevice.deviceId)
    setSelectedDevice(updated)
  }, [devices, selectedDevice?.deviceId])

  const canFilterByValveErrors = hasPermissions(permissions, ['DEVICE:VALVE:COMM'])

  const devicesWithValveErrors = useMemo(
    () => filterDevicesWithValveErrors(devices),
    [devices],
  )

  // Narrows which devices can be picked, not the current selection — toggling this on
  // doesn't clear an already-selected device that happens to fall outside the filter.
  const selectableDevices = errorsOnly && canFilterByValveErrors ? devicesWithValveErrors : devices

  const selectData = useMemo(
    () => selectableDevices.map((device) => ({ value: device.deviceId, label: device.deviceName })),
    [selectableDevices],
  )

  const isSelectDisabled = deviceUpdating || loading || devices.length === 0

  return (
    <Box>
      <Title order={1}>Operate Valves</Title>

      <Stack mt='md' gap='md'>
        {/* Shared device selector — drives both tabs */}
        <Group align='center' gap='md'>
          <Text fw={500}>Device:</Text>
          <Select
            styles={{ dropdown: { zIndex: 2 } }}
            w={300}
            value={selectedDevice?.deviceId ?? null}
            onChange={(value) => setSelectedDevice(devices.find((d) => d.deviceId === value))}
            data={selectData}
            name='device'
            clearable={false}
            searchable
            filter={filterByNameOrId}
            placeholder={accessDenied ? 'No device access' : 'Select a device'}
            // No spinner when access is denied — devices will never arrive, so the loader
            // would sit there indefinitely.
            rightSection={isSelectDisabled && !accessDenied ? <Loader size='xs' /> : undefined}
            nothingFoundMessage={accessDenied ? DEVICE_ACCESS_DENIED_MESSAGE : 'No devices'}
            disabled={isSelectDisabled}
          />

          {accessDenied && (
            <Text
              size='sm'
              c='dimmed'
              role='status'
            >
              {DEVICE_ACCESS_DENIED_MESSAGE}
            </Text>
          )}

          {canFilterByValveErrors && (
            <Switch
              label={`Valve errors only${devicesWithValveErrors.length ? ` (${devicesWithValveErrors.length})` : ''}`}
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.currentTarget.checked)}
              disabled={isSelectDisabled}
            />
          )}
        </Group>

        {selectedDevice ? (
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value='operation' leftSection={<IconAdjustments size={16} />}>
                Valve Operation
              </Tabs.Tab>
              <Tabs.Tab value='schedule' leftSection={<IconCalendarWeek size={16} />}>
                Valve Schedule
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value='operation'>
              <ValveOperationTab
                selectedDevice={selectedDevice}
                devices={devices}
                permissions={permissions}
              />
            </Tabs.Panel>

            <Tabs.Panel value='schedule'>
              <Box mt='md'>
                <ValveScheduleTab device={selectedDevice} />
              </Box>
            </Tabs.Panel>
          </Tabs>
        ) : (
          <Text mt='md' fs='italic' c='dimmed'>Select a device</Text>
        )}
      </Stack>
    </Box>
  )
}

export default ValvesManagement
